/**
 * The by-rule table, sheet 20 callout 9 (mission 6 WP08 T045; FR-012, FR-013,
 * FR-027; NFR-005; ANA.2, ANA.3, ANA.6, GL.2; style guide §7.7; research
 * §1.18, §1.24; post-plan C3).
 *
 * Presentational. One row per (rule, severity) segment, in the API's order,
 * one page of at most 50. The exact `count` sits under "Findings" (register
 * 10). The row and its "Explain ›" link both open the drawer: the row is
 * focusable with Enter and Space but carries no interactive role, because it
 * holds the link (the nested-interactive pattern; axe gates it in WP11). The
 * footer counts rows in register 10's `byRuleFooter` (owner decision
 * 2026-09-26: rows, not the C3 rule count), with the row total only when one is
 * known, and says when the rule, field, segment and text filters only narrowed
 * this page.
 * The description is catalog text, so it may truncate with `title`; no
 * customer value is rendered here.
 * A row with `in_window: 0` (none of its findings falls in the listed window)
 * says register 35's `zeroListed` under its count (mission 8 WP10 T055;
 * FR-020 A-L3).
 */
import type { KeyboardEvent, MouseEvent } from 'react'
import { ChevronLeft, ChevronRight, SearchX, ShieldCheck } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'

import { EmptyState } from '@/components/trellis/EmptyState'
import { SeverityBadge } from '@/components/trellis/SeverityBadge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableSkeletonRows } from '@/components/ui/table'
import type { ByRuleEntry } from '@/lib/api/findings'
import { Mono, count } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import type { Severity } from '@/lib/severity'

import { rowKey, type DrawerTarget } from './useResultsFilters'

export interface ByRuleFooter {
  /** The 1-based position of the page's first row. */
  start: number
  /**
   * The row total, or null when none is truthful: the by-rule body carries none, so it is known only on the
   * last page, unfiltered (owner decision 2026-09-26, decision 4).
   */
  total: number | null
  /** A rule, field, segment or text filter is narrowing the loaded page. */
  filtered: boolean
  /** The run has more than this one page. */
  morePages: boolean
}

export interface ByRulePaging {
  canPrevious: boolean
  canNext: boolean
  onPrevious(): void
  onNext(): void
}

export interface ByRuleTableProps {
  rows: ByRuleEntry[]
  loading: boolean
  footer: ByRuleFooter
  onOpen(rule: string, severity: Severity): void
  selected: DrawerTarget | null
  /** Registers each row element for focus restore when the drawer closes (T047). */
  rowRef(key: string): (el: HTMLTableRowElement | null) => void
  paging: ByRulePaging
}

const COLUMNS = 6

// Placeholders passed to the register's render, then swapped for mono nodes (as TruncationBand does).
const FOOTER_SLOT = { start: '\u0000start\u0000', end: '\u0000end\u0000', total: '\u0000total\u0000' }

function footerSentence(start: number, end: number, total: number | null) {
  const text = PROVISIONAL_COPY.byRuleFooter.render({ ...FOOTER_SLOT, total: total === null ? null : FOOTER_SLOT.total })
  const values: Record<string, number> = { start, end, total: total ?? 0 }
  return text.split('\u0000').map((part, i) => (i % 2 === 1 ? <Mono key={i}>{count(values[part] ?? 0)}</Mono> : part))
}

function isSelected(row: ByRuleEntry, selected: DrawerTarget | null): boolean {
  if (selected === null || selected.rule !== row.rule_id) return false
  return selected.severity === null || selected.severity === row.severity
}

/** "Segment · field", or the segment alone when the rule has no field. */
export function segmentField(segment: string | null, field: string | null): string {
  return [segment, field].filter((part): part is string => part !== null && part !== '').join(' · ')
}

export function Pagination({ paging, className }: { paging: ByRulePaging; className?: string }) {
  if (!paging.canPrevious && !paging.canNext) return null
  return (
    <div className={className ?? 'flex items-center gap-2'}>
      {paging.canPrevious ? (
        <Button variant="secondary" size="sm" aria-label="Previous page" onClick={paging.onPrevious}>
          <ChevronLeft aria-hidden />
        </Button>
      ) : null}
      {paging.canNext ? (
        <Button variant="secondary" size="sm" aria-label="Next page" onClick={paging.onNext}>
          <ChevronRight aria-hidden />
        </Button>
      ) : null}
    </div>
  )
}

export function ByRuleTable({ rows, loading, footer, onOpen, selected, rowRef, paging }: ByRuleTableProps) {
  const location = useLocation()

  function explainTo(row: ByRuleEntry) {
    const next = new URLSearchParams(location.search)
    next.set('rule', row.rule_id)
    next.set('severity', row.severity)
    // mission 6 WP13: under the sheet 23 modal the page is /report-rejection; the drawer lives on the run's results path.
    return { pathname: location.pathname.replace(/\/report-rejection$/, ''), search: `?${next.toString()}` }
  }

  function onKey(event: KeyboardEvent<HTMLTableRowElement>, row: ByRuleEntry) {
    if (event.target !== event.currentTarget) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onOpen(row.rule_id, row.severity)
    }
  }

  function onExplain(event: MouseEvent<HTMLAnchorElement>, row: ByRuleEntry) {
    event.stopPropagation()
    // A modified click (new tab or window) keeps the link's own behaviour.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    onOpen(row.rule_id, row.severity)
  }

  const note = footer.filtered && footer.morePages ? copyText('pageLocalFilterNote') : null

  if (!loading && rows.length === 0) {
    return footer.filtered ? (
      <div>
        {/* mission 6 WP13: handoff from WP08: the results page's own pair, no longer the catalog's. */}
        <EmptyState icon={SearchX} title={copyText('resultsNoMatchTitle')} description={copyText('resultsNoMatchBody')} />
        {note ? <p className="text-caption text-navy-600">{note}</p> : null}
      </div>
    ) : (
      <EmptyState icon={ShieldCheck} title={copyText('zeroFindingsTitle')} description={copyText('zeroFindingsBody')} />
    )
  }

  const end = footer.start + rows.length - 1
  return (
    <div className="flex flex-col gap-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead sort="descending">Severity</TableHead>
            <TableHead>Rule</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Segment · field</TableHead>
            <TableHead className="text-right">{copyText('findingsColumn')}</TableHead>
            <TableHead>
              <span className="sr-only">Explain</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableSkeletonRows columns={COLUMNS} />
          ) : (
            rows.map((row) => {
              const key = rowKey(row.rule_id, row.severity)
              return (
                <TableRow
                  key={key}
                  ref={rowRef(key)}
                  tabIndex={0}
                  data-state={isSelected(row, selected) ? 'selected' : undefined}
                  className="cursor-pointer"
                  onClick={() => onOpen(row.rule_id, row.severity)}
                  onKeyDown={(e) => onKey(e, row)}
                >
                  <TableCell>
                    <SeverityBadge severity={row.severity} />
                  </TableCell>
                  <TableCell>
                    <Mono className="text-body-sm">{row.rule_id}</Mono>
                  </TableCell>
                  <TableCell className="max-w-[320px] truncate" title={row.description ?? undefined}>
                    {row.description}
                  </TableCell>
                  <TableCell className="text-navy-700">{segmentField(row.segment, row.field)}</TableCell>
                  <TableCell numeric>
                    {count(row.count)}
                    {row.in_window === 0 ? (
                      <span className="block font-sans text-caption text-navy-600">{copyText('zeroListed')}</span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-right">
                    <Link
                      to={explainTo(row)}
                      onClick={(e) => onExplain(e, row)}
                      className="whitespace-nowrap rounded-sm text-body-sm text-[var(--color-link)] hover:text-[var(--color-link-hover)] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
                    >
                      Explain ›
                    </Link>
                  </TableCell>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
      {loading ? null : (
        <div data-results-footer="" className="flex items-center justify-between gap-4 text-caption text-navy-600">
          <p>
            {footerSentence(footer.start, end, footer.total)} · sorted by severity, then findings{note ? ` · ${note}` : null}
          </p>
          <Pagination paging={paging} />
        </div>
      )}
    </div>
  )
}
